import React, { useState, useEffect } from 'react';
import { generateCEOName, generateCompanyName } from '../../utils/nameGenerator';
import { ArrowLeft } from 'lucide-react';

interface WelcomePopupProps {
  onStart: (info: { ceoName: string; companyName: string }) => void;
  onBack?: () => void;
}

export function WelcomePopup({ onStart, onBack }: WelcomePopupProps) {
  const [ceoName, setCeoName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [error, setError] = useState('');

  // Prefill names from preferences
  useEffect(() => {
    const savedPrefs = localStorage.getItem('smartphone-simulator-preferences');
    if (savedPrefs) {
      const prefs = JSON.parse(savedPrefs);
      if (prefs.prefillCompanyName) {
        setCompanyName(prefs.prefillCompanyName);
      }
      if (prefs.prefillCEOName) {
        setCeoName(prefs.prefillCEOName);
      }
    }
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!ceoName.trim() || !companyName.trim()) {
      setError('Please fill in both fields');
      return;
    }
    onStart({ ceoName, companyName });
  };

  const handleGenerateCEOName = () => {
    setCeoName(generateCEOName());
    setError('');
  };

  const handleGenerateCompanyName = () => {
    setCompanyName(generateCompanyName());
    setError('');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-900 via-green-900 to-teal-900 flex items-center justify-center">
      <div className="bg-theme-bg-content rounded-lg shadow-xl p-8 max-w-md w-full mx-4 relative">
        {onBack && (
          <button
            onClick={onBack}
            className="absolute top-4 left-4 flex items-center gap-2 text-theme-text-secondary hover:text-theme-text-primary transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
        )}
        <h2 className="text-2xl font-bold text-theme-text-primary mb-6 mt-6">Welcome to Smartphone Tycoon</h2>
        
        <div className="prose prose-sm mb-6">
          <p className="text-theme-text-secondary">
            Start your journey as a mobile device company CEO. Design innovative products,
            manage resources, and compete in the global market.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="ceoName" className="block text-sm font-medium text-theme-text-secondary">
              CEO Name
            </label>
            <div className="mt-1 flex gap-2">
              <input
                type="text"
                id="ceoName"
                data-testid="ceo-name"
                value={ceoName}
                onChange={(e) => setCeoName(e.target.value)}
                className="block w-full rounded-md border-theme-border shadow-sm focus:border-theme-accent focus:ring-theme-accent"
                placeholder="Enter your name"
              />
              <button
                type="button"
                onClick={handleGenerateCEOName}
                data-testid="random-ceo-name"
                className="px-3 py-2 bg-theme-bg-tertiary text-theme-text-secondary rounded-md hover:bg-theme-bg-tertiary transition-colors"
              >
                Random
              </button>
            </div>
          </div>

          <div>
            <label htmlFor="companyName" className="block text-sm font-medium text-theme-text-secondary">
              Company Name
            </label>
            <div className="mt-1 flex gap-2">
              <input
                type="text"
                id="companyName"
                data-testid="company-name"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                className="block w-full rounded-md border-theme-border shadow-sm focus:border-theme-accent focus:ring-theme-accent"
                placeholder="Enter company name"
              />
              <button
                type="button"
                onClick={handleGenerateCompanyName}
                data-testid="random-company-name"
                className="px-3 py-2 bg-theme-bg-tertiary text-theme-text-secondary rounded-md hover:bg-theme-bg-tertiary transition-colors"
              >
                Random
              </button>
            </div>
          </div>

          {error && (
            <p className="text-sm text-theme-error">{error}</p>
          )}

          <button
            type="submit"
            data-testid="start-company"
            className="w-full bg-theme-accent text-white px-4 py-2 rounded-md hover:bg-theme-accent transition-colors"
          >
            Start Game
          </button>
        </form>

        <div className="mt-6 text-sm text-theme-text-muted">
          <h3 className="font-medium text-theme-text-secondary mb-2">Game Features:</h3>
          <ul className="list-disc pl-5 space-y-1">
            <li>Design and develop innovative mobile devices</li>
            <li>Manage production and supply chain</li>
            <li>Research new technologies</li>
            <li>Compete in the global market</li>
            <li>Build your company's reputation</li>
          </ul>
        </div>
      </div>
    </div>
  );
}